import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  RiArrowLeftLine,
  RiRefreshLine,
  RiPieChartLine,
  RiContactsLine,
  RiHistoryLine,
  RiCheckLine,
} from 'react-icons/ri';
import { useGetCampaignQuery } from 'api/metaWhatsappApi';
import useMetaWhatsappSocket from 'views/admin/meta-whatsapp/hooks/useMetaWhatsappSocket';
import {
  Banner,
  CampaignCard,
  CampaignPage,
  ErrorState,
  StatusBadge,
} from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import { statusMeta } from '../_ui/status';
import { CampaignActions } from '../_ui/CampaignActions';
import CampaignActionDialogs from '../_ui/CampaignActionDialogs';
import {
  campaignFromResponse,
  count,
  creatorOf,
  formatDate,
  planningSince,
} from '../_lib/campaignState';
import usePageVisibility from '../hooks/usePageVisibility';
import useCampaignActions from '../hooks/useCampaignActions';
import { pauseReason } from '../campaignErrors';
import CampaignOverview from './Overview';
import CampaignRecipients from './Recipients';
import CampaignHistory from './History';

export default function CampaignDetail() {
  const { id } = useParams();

  // A different id gets a fresh local acknowledgement/dialog state.
  return <CampaignDetailScreen key={id} id={id} />;
}

function CampaignDetailScreen({ id }) {
  const ui = useCampaignUI();
  const navigate = useNavigate();
  const visible = usePageVisibility();
  const [pollMs, setPollMs] = useState(5000);
  const [now, setNow] = useState(Date.now());

  useMetaWhatsappSocket();

  const query = useGetCampaignQuery(id, {
    pollingInterval: visible ? pollMs : 0,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });


  const manager = useCampaignActions({
	refetch: query.refetch,
	fulfilledTimeStamp:
		query.fulfilledTimeStamp,

	onDeleted: () =>
		navigate(
			'/meta-campaigns',
			{
				replace: true,
			},
		),
});

  const server = campaignFromResponse(query.currentData ?? query.data, id);
  const campaign = manager.effective(server);

  useEffect(() => {
    setPollMs(
      ['COMPLETED', 'CANCELLED', 'FAILED_TO_PLAN'].includes(campaign?.state)
        ? 30000
        : 5000,
    );
  }, [campaign?.state]);

  useEffect(() => {
    if (campaign?.state !== 'PLANNING' || !visible) return undefined;

    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [campaign?.state, visible]);

  if (!query.isLoading && !campaign) {
    return (
      <CampaignPage title='Campaign'>
        <CampaignCard>
          <ErrorState
            title='We could not load this campaign'
            description='Check your connection and access, then try again.'
            onRetry={query.refetch}
          />
        </CampaignCard>
      </CampaignPage>
    );
  }

  /**
 * If this campaign was removed from another browser/session, don't continue
 * showing stale controls from RTK Query's cached object.
 */
if (
	Number(
		query.error?.status,
	) === 404
) {
	return (
		<CampaignPage title='Campaign unavailable'>
			<CampaignCard>
				<Text
					fontSize='sm'
					color={
						ui.textSecondary
					}
				>
					This campaign is no longer available. It may have been deleted.
				</Text>

				<Button
					mt={4}
					variant='neutralOutline'
					onClick={() =>
						navigate(
							'/meta-campaigns',
							{
								replace:
									true,
							},
						)
					}
				>
					All campaigns
				</Button>
			</CampaignCard>
		</CampaignPage>
	);
}

  if (!campaign) {
    return (
      <CampaignPage title='Loading campaign'>
        <Skeleton h='140px' borderRadius='2xl' mb={4} />
        <Skeleton h='340px' borderRadius='2xl' />
      </CampaignPage>
    );
  }

  const meta = statusMeta(campaign.state, 'campaign');
  const tone = ui.tones[meta.tone] ?? ui.tones.neutral;
  const creator = creatorOf(campaign);
  const paused =
    campaign.state === 'PAUSED' ? pauseReason(campaign.pauseReason) : null;
  const since = planningSince(campaign);
  const delayed =
    campaign.state === 'PLANNING' && since && now - since > 120000;
  const recipients = campaign.counters?.deduped ?? campaign.counters?.sendable ?? 0;
  const phase = {
    DRAFT: 0,
    PENDING_APPROVAL: 1,
    SCHEDULED: 1,
    PLANNING: 2,
    RUNNING: 3,
    PAUSED: 3,
    COMPLETED: 4,
  }[campaign.state];

  return (
    <CampaignPage
      title={campaign.name || 'Untitled campaign'}
      subtitle={`${campaign.category === 'UTILITY' ? 'Utility' : 'Marketing'} / ${campaign.audience?.type === 'upload' ? 'Uploaded audience' : 'CRM audience'}${campaign.wave > 1 ? ` / Wave ${campaign.wave}` : ''}`}
      actions={
        <Button
          variant='ghost'
          size='sm'
          leftIcon={<RiArrowLeftLine />}
          color={ui.textSecondary}
          onClick={() => navigate('/meta-campaigns')}
        >
          All campaigns
        </Button>
      }
    >
      {query.isError && (
        <Box mb={4}>
          <Banner
            status='warning'
            title='Live status could not be refreshed'
            description='Showing the last confirmed response. Use Refresh status before relying on these figures.'
          />
        </Box>
      )}

      <CampaignCard p={0} overflow='hidden' mb={4}>

        <Flex
          px={{ base: 4, md: 5 }}
          pt={5}
          pb={4}
          justify='space-between'
          align='flex-start'
          gap={4}
          wrap='wrap'
        >
          <Box flex='1 1 280px' minW={0}>
            <HStack mb={2} spacing={3}>
              <StatusBadge status={campaign.state} context='campaign' />
              <Button
                variant='link'
                size='xs'
                color={ui.textSecondary}
                leftIcon={<RiRefreshLine />}
                onClick={query.refetch}
                isLoading={query.isFetching}
              >
                Refresh status
              </Button>
            </HStack>

            <Text
              color={ui.textPrimary}
              fontSize='sm'
              lineHeight='1.7'
              role='status'
              aria-live='polite'
            >
              {meta.hint}
            </Text>

            {campaign.state === 'PLANNING' && (
              <Text color={ui.textMuted} fontSize='xs' mt={2}>
                No second launch is needed. This screen checks for updates every
                5 seconds while visible.
              </Text>
            )}
          </Box>

          <CampaignActions campaign={campaign} manager={manager} />
        </Flex>

        {phase !== undefined && (
          <SimpleGrid
            columns={5}
            px={{ base: 3, md: 5 }}
            pb={4}
            gap={{ base: 1, md: 3 }}
          >
            {['Build', 'Review', 'Prepare', 'Deliver', 'Done'].map((label, index) => (
              <Box key={label}>
                <Box
                  h='3px'
                  borderRadius='full'
                  bg={index <= phase ? tone.fg : ui.borderSubtle}
                  mb={2}
                />
                <HStack spacing={1}>
                  <Icon
                    as={RiCheckLine}
                    display={index < phase ? 'block' : 'none'}
                    color={tone.fg}
                    boxSize={3}
                    aria-hidden='true'
                  />
                  <Text
                    fontSize='xs'
                    fontWeight={index === phase ? 'semibold' : 'normal'}
                    color={index <= phase ? ui.textPrimary : ui.textMuted}
                  >
                    {label}
                  </Text>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        )}

        <SimpleGrid
          columns={{ base: 1, sm: 2, xl: 4 }}
          gap={4}
          bg={ui.surfaceSubtle}
          px={5}
          py={4}
        >
          <Box>
            <Text fontSize='xs' color={ui.textMuted} mb={1}>Created by</Text>
            <HStack spacing={2}>
              <Avatar
                size='xs'
                name={creator.name === 'Name unavailable' ? undefined : creator.name}
                bg={ui.surface}
                color={ui.textSecondary}
              />
              <Text fontSize='sm' fontWeight='semibold' overflowWrap='anywhere'>
                {creator.name}
              </Text>
            </HStack>
          </Box>
          <Box>
            <Text fontSize='xs' color={ui.textMuted} mb={1}>Created</Text>
            <Text fontSize='sm'>{formatDate(campaign.createdAt, true)}</Text>
          </Box>
          <Box>
            <Text fontSize='xs' color={ui.textMuted} mb={1}>Delivery dates</Text>
            <Text fontSize='sm'>
              {formatDate(campaign.schedule?.startDate)}
              {campaign.schedule?.endDate ? ` - ${formatDate(campaign.schedule.endDate)}` : ''}
            </Text>
          </Box>
          <Box>
            <Text fontSize='xs' color={ui.textMuted} mb={1}>Recipient-local hours</Text>
            <Text fontSize='sm'>
              {campaign.schedule?.windowStart ?? '-'} - {campaign.schedule?.windowEnd ?? '-'}
            </Text>
          </Box>
        </SimpleGrid>
      </CampaignCard>

      {Boolean(manager.verifying[id]) && (
        <Box mb={4}>
          <Banner
            status='warning'
            title='Confirming the last action'
            description='The connection was interrupted. Launch is disabled until the campaign status can be confirmed.'
          />
        </Box>
      )}

      {delayed && (
        <Box mb={4}>
          <Banner
            status='warning'
            title='Preparation is taking longer than usual'
            description={`Status checks are still active. Do not launch again. Ask your administrator to inspect the planning job for campaign ${id}. This notice is not a failure result.`}
          />
        </Box>
      )}

      {campaign.emergencyStopped && (
        <Box mb={4}>
          <Banner
            status='error'
            title='Workspace emergency stop is active'
            description='New campaign sends are stopped across this workspace.'
          />
        </Box>
      )}

      {paused && (
        <Box mb={4}>
          <Banner
            status={paused.tone === 'danger' ? 'error' : paused.tone === 'warning' ? 'warning' : 'info'}
            description={paused.text}
          />
        </Box>
      )}

      {campaign.plan?.error && (
        <Box mb={4}>
          <Banner
            status='error'
            title='Preparation needs attention'
            description={campaign.plan.error}
          >
            <Text fontSize='xs' mt={2}>
              Use Actions / Duplicate as draft to prepare a corrected campaign.
              Duplicating does not launch it.
            </Text>
          </Banner>
        </Box>
      )}

      {campaign.plan?.warning && (
        <Box mb={4}>
          <Banner
            status='warning'
            title='Some recipients do not fit the delivery window'
            description='Review the delivery plan and recipient outcomes. Messages outside the final allowed date can expire.'
          />
        </Box>
      )}

      <CampaignCard p={0} overflow='hidden'>
        <Tabs isLazy lazyBehavior='unmount' >
          <TabList px={3} pt={2} borderColor={ui.borderSubtle} overflowX='auto'>
            {[
              { label: 'Overview', icon: RiPieChartLine },
              { label: `Recipients (${count(recipients)})`, icon: RiContactsLine },
              { label: 'Activity & creation', icon: RiHistoryLine },
            ].map((tab) => (
              <Tab
                key={tab.label}
                color={ui.textSecondary}
                whiteSpace='nowrap'
                fontSize='sm'
                borderRadius='lg'
                mb={2}
                _selected={{
                  bg: ui.accentSubtle,
                  color: ui.textPrimary,
                  borderColor: 'transparent',
                  fontWeight: 'semibold',
                }}
              >
                <HStack spacing={2}>
                  <Icon as={tab.icon} aria-hidden='true' />
                  <Text>{tab.label}</Text>
                </HStack>
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            <TabPanel px={{ base: 3, md: 5 }}>
              <CampaignOverview campaign={campaign} />
            </TabPanel>
            <TabPanel px={{ base: 3, md: 5 }}>
              <CampaignRecipients campaign={campaign} />
            </TabPanel>
            <TabPanel px={{ base: 3, md: 5 }}>
              <CampaignHistory campaign={campaign} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </CampaignCard>

      <Text fontSize='xs' color={ui.textMuted} mt={3}>
        Campaign ID: {id}. Creation and activity timestamps use your browser's time zone.
      </Text>
      <CampaignActionDialogs manager={manager} />
    </CampaignPage>
  );
}
